import type { ObservationRecord } from '../../types/observations';
import type { WearableMetricId } from '../../data/wearableMetrics';
import { WEARABLE_METRIC_DEFINITIONS } from '../../data/wearableMetrics';
import {
  formatWearableMetricValue,
  matchMetricObservation,
  normalizeMetricValue,
} from '../../services/memberWearableMetricsService';
import { coerceDate } from '../dateLabels';
import type { BiomarkerMovement } from './biomarkerDeltas';

/**
 * Period-over-period movement in the wearable metrics — "resting heart rate
 * down 4 bpm on a 30-day average".
 *
 * This is new work rather than a wrapper. Two nearby things look like they
 * already do it and do not: `buildWearableMetricDatasets` exposes a
 * `deltaLabel` that is really just a point count ("30 data points"), and
 * `buildWearableInsightBundle` computes a trailing 30-day average with nothing
 * to compare it against. A real delta needs the PRIOR window too.
 *
 * Both windows must be reasonably populated before anything is reported. A
 * fortnight of holiday with the watch in a drawer would otherwise read as a
 * dramatic physiological shift.
 *
 * Movement uses the same vocabulary as the biomarker deltas — 'toward_range' /
 * 'away_from_range' / 'stable', never 'improved' / 'worsened' — and metrics
 * whose definition marks them `contextual` (day strain, skin temperature) never
 * claim a direction of travel at all.
 */

export interface WearableDelta {
  metricId: WearableMetricId;
  label: string;
  from: number;
  to: number;
  absChange: number;
  pctChange: number | null;
  /** Preformatted in the metric's own unit, e.g. "58 bpm" / "7.4h". */
  fromLabel: string;
  toLabel: string;
  changeLabel: string;
  arrow: '↑' | '↓' | '→';
  movement: BiomarkerMovement;
  currentDays: number;
  priorDays: number;
}

export interface WearableDeltaOptions {
  windowDays?: number;
  /** Days of data required in EACH window before a delta is reported. */
  minDaysPerWindow?: number;
  minPctChange?: number;
  limit?: number;
}

const DEFAULTS = { windowDays: 30, minDaysPerWindow: 10, minPctChange: 5, limit: 4 };
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** One value per day, so a device reporting hourly doesn't outweigh a daily one. */
function dailyMeans(values: { day: string; value: number }[]): number[] {
  const byDay = new Map<string, number[]>();
  for (const { day, value } of values) {
    const bucket = byDay.get(day);
    if (bucket) bucket.push(value);
    else byDay.set(day, [value]);
  }
  return [...byDay.values()].map((dayValues) => dayValues.reduce((a, b) => a + b, 0) / dayValues.length);
}

const mean = (values: number[]) => values.reduce((a, b) => a + b, 0) / values.length;

function resolveMovement(
  absChange: number,
  pctChange: number | null,
  comparisonIsBetter: 'higher' | 'lower' | 'contextual',
  minPctChange: number,
): BiomarkerMovement {
  if (pctChange === null || Math.abs(pctChange) < minPctChange) return 'stable';
  if (comparisonIsBetter === 'contextual') return 'stable';
  const rising = absChange > 0;
  return comparisonIsBetter === 'higher'
    ? (rising ? 'toward_range' : 'away_from_range')
    : (rising ? 'away_from_range' : 'toward_range');
}

export function buildWearableDeltas(
  observations: ObservationRecord[],
  now: Date = new Date(),
  options: WearableDeltaOptions = {},
): WearableDelta[] {
  const windowDays = options.windowDays ?? DEFAULTS.windowDays;
  const minDaysPerWindow = options.minDaysPerWindow ?? DEFAULTS.minDaysPerWindow;
  const minPctChange = options.minPctChange ?? DEFAULTS.minPctChange;
  const limit = options.limit ?? DEFAULTS.limit;

  const currentStart = now.getTime() - windowDays * MS_PER_DAY;
  const priorStart = now.getTime() - 2 * windowDays * MS_PER_DAY;

  const deltas: WearableDelta[] = [];

  for (const definition of Object.values(WEARABLE_METRIC_DEFINITIONS)) {
    // Sleep stages is a stacked composition, not a single quantity to average.
    if (definition.id === 'sleep_stages') continue;

    const current: { day: string; value: number }[] = [];
    const prior: { day: string; value: number }[] = [];

    for (const observation of observations) {
      if (!matchMetricObservation(definition.id, observation)) continue;
      const value = normalizeMetricValue(definition.id, observation);
      if (value == null) continue;
      const at = coerceDate(observation.observedAt)?.getTime();
      if (at == null) continue;

      // Half-open windows: (currentStart, now] and (priorStart, currentStart].
      // A reading exactly `windowDays` old belongs to the PRIOR window — "the
      // last 30 days" means days 0-29. With both bounds inclusive it would fall
      // into the current window and quietly contaminate the comparison.
      const day = new Date(at).toISOString().slice(0, 10);
      if (at > currentStart && at <= now.getTime()) current.push({ day, value });
      else if (at > priorStart && at <= currentStart) prior.push({ day, value });
    }

    const currentDaily = dailyMeans(current);
    const priorDaily = dailyMeans(prior);
    if (currentDaily.length < minDaysPerWindow || priorDaily.length < minDaysPerWindow) continue;

    const from = mean(priorDaily);
    const to = mean(currentDaily);
    const absChange = to - from;
    const pctChange = from === 0 ? null : (absChange / Math.abs(from)) * 100;

    deltas.push({
      metricId: definition.id,
      label: definition.label,
      from,
      to,
      absChange,
      pctChange,
      fromLabel: formatWearableMetricValue(definition.id, from),
      toLabel: formatWearableMetricValue(definition.id, to),
      changeLabel: formatWearableMetricValue(definition.id, Math.abs(absChange)),
      arrow: absChange === 0 ? '→' : absChange > 0 ? '↑' : '↓',
      movement: resolveMovement(absChange, pctChange, definition.comparisonIsBetter, minPctChange),
      currentDays: currentDaily.length,
      priorDays: priorDaily.length,
    });
  }

  return deltas
    .sort((left, right) => Math.abs(right.pctChange ?? 0) - Math.abs(left.pctChange ?? 0))
    .slice(0, limit);
}
